import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { translations } from '../i18n/translations';

const STORAGE_KEY = 'oltre-frontiera-lang';

function readLangFromUrl() {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const q = String(params.get('lang') || params.get('language') || '')
      .toLowerCase()
      .trim();
    if (q === 'it' || q === 'fr') return q;
  } catch {
    /* ignore */
  }
  return null;
}

const LanguageContext = createContext({
  language: 'fr',
  setLanguage: () => {},
  t: (key, _vars) => key,
  locale: 'fr-FR',
});

function getByPath(obj, path) {
  const parts = path.split('.');
  let cur = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = cur[p];
  }
  return cur;
}

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    const fromUrl = readLangFromUrl();
    if (fromUrl === 'it' || fromUrl === 'fr') return fromUrl;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'it' || stored === 'fr') return stored;
    } catch {
      /* ignore */
    }
    return 'fr';
  });

  const setLanguage = useCallback((lang) => {
    if (lang !== 'fr' && lang !== 'it') return;
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
  }, []);

  const locale = language === 'it' ? 'it-IT' : 'fr-FR';

  const t = useCallback(
    (key, vars) => {
      let val = getByPath(translations[language], key);
      if (typeof val !== 'string') return key;
      if (vars && typeof vars === 'object') {
        Object.keys(vars).forEach((k) => {
          val = val.replace(new RegExp(`\\{${k}\\}`, 'g'), String(vars[k]));
        });
      }
      return val;
    },
    [language]
  );

  useEffect(() => {
    document.documentElement.lang = language === 'it' ? 'it' : 'fr';
  }, [language]);

  const value = useMemo(
    () => ({ language, setLanguage, t, locale }),
    [language, setLanguage, t, locale]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  return useContext(LanguageContext);
}
